import { useCallback, useEffect, useState } from 'react';
import { DownloadSimple, Key, Plug, Plus, ShieldCheck, Trash } from '@phosphor-icons/react';
import { useWorkspace } from '../App';
import { api, ApiError } from '../lib/api';
import { actionLabel, faNumber, formatBytes, jalaliDate, statusLabel } from '../lib/format';
import { Card, InlineNotice, LoadingState, PageHeader, StatusBadge } from '../components/Ui';
import { CustomSelect, SmartDateField, SmartInput } from '../components/SmartForm';
import { ChangePasswordForm, passwordProblem } from '../components/ChangePasswordForm';
import { PasswordField } from '../components/PasswordField';
import type {
  ActivityEntry, Actor, BackupRecord, Connection, OrganizationSettings, ReportTemplate, StoredRule, Theme,
} from '../types';

type PanelId = 'general' | 'password' | 'users' | 'rules' | 'templates' | 'connections' | 'backups' | 'activity' | 'about';

const PANELS: Array<{ id: PanelId; label: string; permission?: string }> = [
  { id: 'general', label: 'تنظیمات عمومی' },
  // No permission: an account's own password is not an administrative power.
  { id: 'password', label: 'رمز عبور' },
  { id: 'users', label: 'کاربران و نقش‌ها', permission: 'users.read' },
  { id: 'rules', label: 'قوانین تحلیل', permission: 'rules.write' },
  { id: 'templates', label: 'گزارش و قالب‌ها', permission: 'templates.write' },
  { id: 'connections', label: 'اتصال‌ها' },
  { id: 'backups', label: 'پشتیبان‌گیری', permission: 'system.backup' },
  { id: 'activity', label: 'گزارش فعالیت', permission: 'users.read' },
  { id: 'about', label: 'نسخه و پشتیبانی' },
];

/** §۷ گزارش و قالب‌ها names these four; the letterhead applies to all of them. */
const TEMPLATE_KINDS: Array<{ key: string; labelFa: string }> = [
  { key: 'letterhead', labelFa: 'سربرگ' },
  { key: 'board', labelFa: 'گزارش هیئت‌مدیره' },
  { key: 'audit', labelFa: 'گزارش حسابرسی' },
  { key: 'tax', labelFa: 'گزارش مالیاتی' },
];

export function SettingsPage() {
  const { bootstrap, can, refresh } = useWorkspace();
  const [panel, setPanel] = useState<PanelId>('general');
  const [settings, setSettings] = useState<OrganizationSettings | null>(null);
  const [version, setVersion] = useState(0);
  const [users, setUsers] = useState<Actor[]>([]);
  const [rules, setRules] = useState<StoredRule[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [backups, setBackups] = useState<BackupRecord[]>([]);
  const [disk, setDisk] = useState({ count: 0, bytes: 0 });
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Depend on the booleans, never on `can` itself: the workspace hands back a
  // fresh function every render, so a dependency on it would re-run this effect
  // forever and the page would never leave its loading state.
  const canReadUsers = can('users.read');
  const canWriteRules = can('rules.write');
  const canBackup = can('system.backup');

  const load = useCallback(async () => {
    const current = await api.settings();
    setSettings(current.settings);
    setVersion(current.version);
    setConnections((await api.connections()).connections);
    setTemplates((await api.templates()).templates);
    if (canReadUsers) {
      setUsers((await api.users()).users);
      setActivity((await api.activity(60)).activity);
    }
    if (canWriteRules) setRules((await api.rules()).rules);
    if (canBackup) {
      const result = await api.backups();
      setBackups(result.backups);
      setDisk(result.disk);
    }
  }, [canReadUsers, canWriteRules, canBackup]);

  useEffect(() => {
    setLoading(true);
    void load().catch((caught: Error) => setError(caught.message)).finally(() => setLoading(false));
  }, [load]);

  async function saveSettings(patch: Partial<OrganizationSettings>) {
    if (!settings) return;
    setError('');
    setMessage('');
    try {
      const result = await api.updateSettings({ ...patch, expectedVersion: version });
      setSettings(result.settings);
      setVersion(result.version);
      setMessage('تنظیمات ذخیره شد.');
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'تنظیمات ذخیره نشد.');
    }
  }

  if (loading || !settings) return <LoadingState label="در حال بارگذاری تنظیمات" />;

  const visiblePanels = PANELS.filter((item) => !item.permission || can(item.permission));

  return (
    <>
      <PageHeader
        eyebrow="مدیریت و تنظیمات"
        title={bootstrap.organization.name}
        description="پیکربندی سازمان، کاربران، قوانین تحلیل و نسخه‌های پشتیبان."
      />

      {error && <InlineNotice tone="danger">{error}</InlineNotice>}
      {message && <InlineNotice tone="success">{message}</InlineNotice>}

      <div className="settings-layout">
        <nav className="settings-nav" aria-label="بخش‌های تنظیمات">
          {visiblePanels.map((item) => (
            <button key={item.id} className={panel === item.id ? 'active' : ''} onClick={() => setPanel(item.id)}>
              {item.label}
            </button>
          ))}
        </nav>

        <div className="settings-panels">
          {panel === 'general' && (
            <GeneralPanel settings={settings} onSave={saveSettings} canWrite={can('settings.write')} />
          )}
          {panel === 'password' && <PasswordPanel />}
          {panel === 'users' && <UsersPanel users={users} onChanged={load} canWrite={can('users.write')} roles={bootstrap.capabilities.roles} />}
          {panel === 'rules' && <RulesPanel rules={rules} onChanged={load} />}
          {panel === 'templates' && <TemplatesPanel templates={templates} onChanged={load} canWrite={can('templates.write')} />}
          {panel === 'connections' && <ConnectionsPanel connections={connections} />}
          {panel === 'backups' && <BackupsPanel backups={backups} disk={disk} onChanged={load} />}
          {panel === 'activity' && <ActivityPanel activity={activity} />}
          {panel === 'about' && <AboutPanel version={bootstrap.organization.version} />}
        </div>
      </div>
    </>
  );
}

/**
 * Changing your own password.
 *
 * The endpoint and the API client method for this both already existed; what
 * was missing was any screen that called them, so the only way to rotate a
 * password was a direct write to the database.
 */
function PasswordPanel() {
  const [done, setDone] = useState(false);

  return (
    <Card>
      <div className="card-heading"><h2>رمز عبور</h2></div>
      {done ? (
        <InlineNotice tone="success">رمز عبور عوض شد. برای ادامه دوباره وارد شوید.</InlineNotice>
      ) : (
        <ChangePasswordForm onChanged={() => setDone(true)} />
      )}
    </Card>
  );
}

/**
 * The OpenRouter model chain used to be edited here. It is deliberately gone:
 * which model answers is an operational decision made in the server env
 * (`FINOJAN_OPENROUTER_MODELS`), not a setting an accountant should be asked
 * about — and the textarea never actually changed the model anyway, since the
 * client is built from the env list at boot.
 */
function GeneralPanel({ settings, onSave, canWrite }: {
  settings: OrganizationSettings;
  onSave: (patch: Partial<OrganizationSettings>) => Promise<void>;
  canWrite: boolean;
}) {
  return (
    <Card>
      <div className="card-heading"><h2>تنظیمات عمومی</h2></div>
      <div className="form-grid">
        <div className="field">
          <span className="smart-field__label">واحد پول پیش‌فرض</span>
          <CustomSelect
            ariaLabel="واحد پول پیش‌فرض"
            value={settings.displayCurrency}
            disabled={!canWrite}
            onChange={(value) => onSave({ displayCurrency: value as 'toman' | 'rial' })}
            options={[{ value: 'rial', label: 'ریال' }, { value: 'toman', label: 'تومان' }]}
          />
        </div>
        <div className="field">
          <span className="smart-field__label">پوسته پیش‌فرض سازمان</span>
          <CustomSelect
            ariaLabel="پوسته پیش‌فرض سازمان"
            value={settings.theme}
            disabled={!canWrite}
            onChange={(value) => onSave({ theme: value as Theme })}
            options={[
              { value: 'system', label: 'هماهنگ با سیستم' },
              { value: 'light', label: 'روشن' },
              { value: 'dark', label: 'تیره' },
            ]}
          />
          {/* Says out loud why this control can look like it did nothing: the
              toggle in نوار بالا is a per-device override and outranks it. */}
          <p className="hint">
            این مقدار برای دستگاه‌هایی است که پوسته‌ای انتخاب نکرده‌اند. کلید تغییر پوسته در نوار
            بالا فقط همین مرورگر را عوض می‌کند و بر این مقدار مقدم است.
          </p>
        </div>
        <div className="field">
          <span className="smart-field__label">حالت پیش‌فرض تحلیل</span>
          <CustomSelect
            ariaLabel="حالت پیش‌فرض تحلیل"
            value={settings.defaultAiMode}
            disabled={!canWrite}
            onChange={(value) => onSave({ defaultAiMode: value as OrganizationSettings['defaultAiMode'] })}
            options={[
              { value: 'management', label: 'مدیریتی' },
              { value: 'audit', label: 'حسابرسی' },
              { value: 'tax', label: 'مالیاتی' },
              { value: 'conservative', label: 'محافظه‌کارانه' },
              { value: 'custom', label: 'کاربری' },
            ]}
          />
        </div>
        <div className="field">
          <span className="smart-field__label">سطح پیش‌فرض گزارش</span>
          <CustomSelect
            ariaLabel="سطح پیش‌فرض گزارش"
            value={settings.defaultReportDetail}
            disabled={!canWrite}
            onChange={(value) => onSave({ defaultReportDetail: value as OrganizationSettings['defaultReportDetail'] })}
            options={[
              { value: 'executive', label: 'خلاصه مدیریتی' },
              { value: 'standard', label: 'استاندارد' },
              { value: 'full', label: 'کامل' },
            ]}
          />
        </div>
        <SmartDateField
          id="settings-fiscal-start"
          label="شروع پیش‌فرض سال مالی"
          hint="این تاریخ فقط مقدار اولیه فرم ثبت شرکت است و سال مالی شرکت‌های موجود را تغییر نمی‌دهد."
          value={settings.defaultFiscalYearStartIso}
          disabled={!canWrite}
          onChange={(iso) => onSave({ defaultFiscalYearStartIso: iso })}
        />
      </div>
    </Card>
  );
}

function UsersPanel({ users, onChanged, canWrite, roles }: {
  users: Actor[];
  onChanged: () => Promise<void>;
  canWrite: boolean;
  roles: Array<{ key: string; labelFa: string }>;
}) {
  const [draft, setDraft] = useState({ username: '', displayName: '', mobile: '', role: 'viewer', password: '' });
  const [error, setError] = useState('');
  const [resetting, setResetting] = useState('');
  const [temporary, setTemporary] = useState('');
  const [resetDone, setResetDone] = useState('');

  async function resetPassword(user: Actor, event: React.FormEvent) {
    event.preventDefault();
    setError('');
    setResetDone('');
    const problem = passwordProblem(temporary);
    if (problem) { setError(problem); return; }
    try {
      await api.resetUserPassword(user.id, temporary);
      // The server marks it must-change, so this really is temporary: the next
      // sign-in for that account cannot get past the change screen.
      setResetDone(user.username);
      setResetting('');
      setTemporary('');
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'رمز بازنشانی نشد.');
    }
  }

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    try {
      await api.createUser(draft);
      setDraft({ username: '', displayName: '', mobile: '', role: 'viewer', password: '' });
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'کاربر ساخته نشد.');
    }
  }

  async function change(user: Actor, patch: Record<string, unknown>) {
    setError('');
    try {
      await api.updateUser(user.id, { ...patch, expectedVersion: 0 });
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'تغییر کاربر ثبت نشد.');
    }
  }

  return (
    <>
      <Card>
        <div className="card-heading"><h2>کاربران</h2><span className="data-badge">{faNumber(users.length)}</span></div>
        {error && <InlineNotice tone="danger">{error}</InlineNotice>}
        {resetDone && <InlineNotice tone="success">رمز موقت برای «{resetDone}» ثبت شد. آن را از یک راه امن به او برسانید.</InlineNotice>}
        <div className="data-table">
          <table>
            <thead><tr><th>کاربر</th><th>نقش</th><th>وضعیت</th><th>آخرین ورود</th>{canWrite && <th>رمز عبور</th>}</tr></thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.displayName || user.username}</strong>
                    {/* Only when it adds something: with no display name the
                        username was printed twice, one above the other. */}
                    {user.displayName && <small dir="ltr">{user.username}</small>}
                  </td>
                  <td>
                    <CustomSelect
                      ariaLabel={`نقش ${user.username}`}
                      value={user.role}
                      disabled={!canWrite}
                      onChange={(role) => change(user, { role })}
                      options={roles.map((role) => ({ value: role.key, label: role.labelFa }))}
                    />
                  </td>
                  <td><StatusBadge status={user.status} label={statusLabel(user.status)} /></td>
                  <td>{jalaliDate(user.lastLoginAt)}</td>
                  {canWrite && (
                    <td>
                      <button
                        type="button"
                        className="button button--ghost button--small"
                        onClick={() => { setResetting(resetting === user.id ? '' : user.id); setTemporary(''); setError(''); }}
                      >
                        <Key size={15} /> بازنشانی
                      </button>
                    </td>
                  )}
                </tr>
              )).flatMap((row, index) => {
                const user = users[index];
                if (!canWrite || resetting !== user.id) return [row];
                return [row, (
                  <tr key={`${user.id}-reset`}>
                    <td colSpan={5}>
                      <form className="form-grid" onSubmit={(event) => resetPassword(user, event)}>
                        <PasswordField
                          id={`reset-${user.id}`}
                          label={`رمز موقت برای ${user.displayName || user.username}`}
                          hint="حداقل ۱۰ نویسه با حرف و رقم. کاربر در ورود بعدی باید آن را عوض کند."
                          value={temporary}
                          onValueChange={setTemporary}
                        />
                        <div className="span-full">
                          <button className="button button--primary button--small" type="submit">ثبت رمز موقت</button>
                        </div>
                      </form>
                    </td>
                  </tr>
                )];
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {canWrite && (
        <Card>
          <div className="card-heading"><h2>کاربر جدید</h2></div>
          <form className="form-grid" onSubmit={create}>
            <SmartInput id="user-username" label="نام کاربری" hint="فقط حروف لاتین، رقم، نقطه، خط تیره و زیرخط. بزرگی و کوچکی حروف اهمیتی ندارد." code value={draft.username} onValueChange={(value) => setDraft({ ...draft, username: value })} required />
            <SmartInput id="user-display" label="نام و نام خانوادگی" hint="نامی که در سامانه نمایش داده می‌شود." value={draft.displayName} onValueChange={(value) => setDraft({ ...draft, displayName: value })} />
            <SmartInput id="user-mobile" label="شماره موبایل" hint="برای تماس مدیر سامانه در زمان بازنشانی رمز." code value={draft.mobile} onValueChange={(value) => setDraft({ ...draft, mobile: value })} />
            <PasswordField id="user-password" label="رمز عبور موقت" hint="حداقل ۱۰ نویسه با حرف و رقم؛ کاربر در ورود اول آن را عوض می‌کند." value={draft.password} onValueChange={(value) => setDraft({ ...draft, password: value })} />
            <div className="field">
              <span className="smart-field__label">نقش</span>
              <CustomSelect
                ariaLabel="نقش کاربر جدید"
                value={draft.role}
                onChange={(role) => setDraft({ ...draft, role })}
                options={roles.map((role) => ({ value: role.key, label: role.labelFa }))}
              />
            </div>
            <div className="span-full">
              <button className="button button--primary" type="submit"><Plus size={15} /> ساخت کاربر</button>
            </div>
          </form>
        </Card>
      )}
    </>
  );
}

function RulesPanel({ rules, onChanged }: { rules: StoredRule[]; onChanged: () => Promise<void> }) {
  const [error, setError] = useState('');

  async function remove(rule: StoredRule) {
    setError('');
    try {
      await api.deleteRule(rule.id);
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'قانون حذف نشد.');
    }
  }

  async function toggle(rule: StoredRule) {
    setError('');
    try {
      await api.saveRule({ ...rule, enabled: !rule.enabled });
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'وضعیت قانون تغییر نکرد.');
    }
  }

  const kindLabel = (kind: string) => ({ mapping: 'نگاشت حساب', kpi: 'شاخص', alert: 'هشدار' } as Record<string, string>)[kind] ?? kind;

  return (
    <Card>
      <div className="card-heading"><h2>قوانین تحلیل</h2><span className="data-badge">{faNumber(rules.length)}</span></div>
      {error && <InlineNotice tone="danger">{error}</InlineNotice>}
      <p className="hint">
        قوانین سازمانی روی همه شرکت‌ها اعمال می‌شوند. قانون نگاشت طبقه حساب را تعیین می‌کند، شاخص یک
        نسبت تازه می‌سازد و هشدار روی یک شاخص موجود آستانه می‌گذارد.
      </p>
      {rules.length === 0 ? (
        <p className="hint">هنوز قانون سفارشی‌ای تعریف نشده است؛ قوانین پیش‌فرض موتور همیشه فعال‌اند.</p>
      ) : (
        <div className="data-table">
          <table>
            <thead><tr><th>عنوان</th><th>نوع</th><th>وضعیت</th><th /></tr></thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id}>
                  <td><strong>{rule.labelFa}</strong><small dir="ltr">{rule.key}</small></td>
                  <td>{kindLabel(rule.kind)}</td>
                  <td><StatusBadge status={rule.enabled ? 'active' : 'archived'} label={rule.enabled ? 'فعال' : 'غیرفعال'} /></td>
                  <td>
                    <button className="button button--ghost button--small" onClick={() => toggle(rule)}>
                      {rule.enabled ? 'غیرفعال کن' : 'فعال کن'}
                    </button>
                    {!rule.builtin && (
                      <button className="button button--ghost button--small" onClick={() => remove(rule)}><Trash size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

interface TemplateDraft {
  labelFa: string;
  organizationFa: string;
  addressFa: string;
  contactFa: string;
  noteFa: string;
  /** One section per line, exactly as typed. Split only on save. */
  sections: string;
}

function draftOf(template: ReportTemplate | undefined, fallbackLabel: string): TemplateDraft {
  return {
    labelFa: template?.labelFa ?? fallbackLabel,
    organizationFa: template?.letterhead.organizationFa ?? '',
    addressFa: template?.letterhead.addressFa ?? '',
    contactFa: template?.letterhead.contactFa ?? '',
    noteFa: template?.letterhead.noteFa ?? '',
    sections: (template?.sections ?? []).join('\n'),
  };
}

/**
 * §۷ گزارش و قالب‌ها — سربرگ، گزارش هیئت‌مدیره، گزارش حسابرسی، گزارش مالیاتی.
 *
 * The letterhead is what a filed report is signed under, so it is edited as
 * plain named fields rather than free markup: an organisation name that came
 * out of a rich-text box is a place for a mistake to hide.
 */
function TemplatesPanel({
  templates,
  onChanged,
  canWrite,
}: {
  templates: ReportTemplate[];
  onChanged: () => Promise<void>;
  canWrite: boolean;
}) {
  const [kind, setKind] = useState(TEMPLATE_KINDS[0].key);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const existing = templates.find((template) => template.kind === kind);
  const definition = TEMPLATE_KINDS.find((item) => item.key === kind) ?? TEMPLATE_KINDS[0];

  const [draft, setDraft] = useState(() => draftOf(existing, definition.labelFa));
  // Reload the form whenever the selected template — or the saved copy of it —
  // changes. Without this, switching kinds would leave the previous template's
  // letterhead in the fields and save it onto this one at the next click.
  useEffect(() => {
    setDraft(draftOf(existing, definition.labelFa));
  }, [kind, existing?.updatedAt]);

  const set = (field: keyof TemplateDraft) => (value: string) => setDraft((current) => ({ ...current, [field]: value }));

  return (
    <Card>
      <div className="card-heading">
        <h2>گزارش و قالب‌ها</h2>
        {existing && <span className="data-badge">آخرین ویرایش {jalaliDate(existing.updatedAt)}</span>}
      </div>
      <p className="hint">
        سربرگ و بخش‌های هر قالب، پایه گزارش‌هایی است که در «گزارش‌های تحلیلی» ساخته می‌شود.
      </p>

      <div className="segmented" role="group" aria-label="نوع قالب">
        {TEMPLATE_KINDS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={kind === item.key ? 'active' : ''}
            aria-pressed={kind === item.key}
            onClick={() => { setKind(item.key); setError(''); setMessage(''); }}
          >
            {item.labelFa}
          </button>
        ))}
      </div>

      {error && <InlineNotice tone="danger">{error}</InlineNotice>}
      {message && <InlineNotice tone="success">{message}</InlineNotice>}

      <form
        className="template-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError('');
          setMessage('');
          try {
            await api.saveTemplate({
              kind,
              labelFa: draft.labelFa.trim() || definition.labelFa,
              letterhead: {
                organizationFa: draft.organizationFa.trim(),
                addressFa: draft.addressFa.trim(),
                contactFa: draft.contactFa.trim(),
                noteFa: draft.noteFa.trim(),
              },
              // One section per line is the whole editor. A section list is an
              // ordered list of headings and nothing else.
              sections: draft.sections.split('\n').map((line) => line.trim()).filter(Boolean).slice(0, 30),
            });
            setMessage('قالب ذخیره شد.');
            await onChanged();
          } catch (caught) {
            setError(caught instanceof ApiError ? caught.message : 'قالب ذخیره نشد.');
          } finally {
            setBusy(false);
          }
        }}
      >
        <SmartInput id="template-label" label="عنوان قالب" value={draft.labelFa} onValueChange={set('labelFa')} hint="عنوانی که بالای گزارش چاپ می‌شود." disabled={!canWrite} />
        <SmartInput id="template-organization" label="نام سازمان در سربرگ" value={draft.organizationFa} onValueChange={set('organizationFa')} hint="نام حقوقی که گزارش با آن منتشر می‌شود." disabled={!canWrite} />
        <SmartInput id="template-address" label="نشانی" value={draft.addressFa} onValueChange={set('addressFa')} hint="نشانی چاپ‌شده در پای سربرگ." disabled={!canWrite} />
        <SmartInput id="template-contact" label="اطلاعات تماس" value={draft.contactFa} onValueChange={set('contactFa')} hint="تلفن یا ایمیل مرجع گزارش." disabled={!canWrite} />
        <label className="field" htmlFor="template-note">
          <span className="smart-field__label">یادداشت پای گزارش</span>
          <span className="smart-field__control smart-field__control--boxed">
            <textarea id="template-note" rows={2} value={draft.noteFa} onChange={(event) => set('noteFa')(event.target.value)} disabled={!canWrite} />
          </span>
          <small className="smart-field__message"><span>مثلاً «این گزارش برای استفاده داخلی تهیه شده است».</span></small>
        </label>
        <label className="field" htmlFor="template-sections">
          <span className="smart-field__label">بخش‌های گزارش</span>
          <span className="smart-field__control smart-field__control--boxed">
            <textarea id="template-sections" rows={6} value={draft.sections} onChange={(event) => set('sections')(event.target.value)} disabled={!canWrite} />
          </span>
          <small className="smart-field__message"><span>هر خط یک بخش. ترتیب خطوط همان ترتیب بخش‌ها در گزارش است.</span></small>
        </label>

        {canWrite ? (
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? 'در حال ذخیره…' : 'ذخیره قالب'}
          </button>
        ) : (
          <p className="hint">برای ویرایش قالب‌ها به دسترسی «قالب‌ها» نیاز دارید.</p>
        )}
      </form>
    </Card>
  );
}

/** §۷ امنیت و پشتیبانی — نسخه نرم‌افزار و راه تماس با پشتیبانی. */
function AboutPanel({ version }: { version: string }) {
  return (
    <Card>
      <div className="card-heading"><h2>نسخه و پشتیبانی</h2></div>
      <div className="health-list">
        <div className="health-list__row">
          <span>نسخه نرم‌افزار</span>
          <b dir="ltr">{version}</b>
        </div>
        <div className="health-list__row">
          <span>پشتیبانی</span>
          <b>از طریق مدیر سامانه سازمان شما</b>
        </div>
      </div>
      <p className="hint">
        هنگام گزارش یک مشکل، شماره نسخه بالا را همراه شرح مشکل بفرستید؛ رفتار هر نسخه با نسخه دیگر
        فرق می‌کند و بدون آن پیگیری از ابتدا شروع می‌شود.
      </p>
    </Card>
  );
}

function ConnectionsPanel({ connections }: { connections: Connection[] }) {
  return (
    <Card>
      <div className="card-heading"><h2>اتصال‌ها</h2></div>
      <p className="hint">
        {/* Only working routes are listed. A row labelled «به‌زودی» is a button
            someone clicks and waits on, which is worse than its absence. */}
        فقط راه‌های ورود داده‌ای که هم‌اکنون کار می‌کنند اینجا فهرست می‌شوند.
      </p>
      <div className="health-list">
        {connections.map((connection) => (
          <div key={connection.id} className="health-list__row">
            <span><Plug size={16} /> {connection.labelFa}</span>
            <StatusBadge status={connection.status} label={statusLabel(connection.status)} />
            <b>{connection.lastUsedAt ? `آخرین استفاده ${jalaliDate(connection.lastUsedAt)}` : 'تاکنون استفاده نشده'}</b>
          </div>
        ))}
      </div>
    </Card>
  );
}

function BackupsPanel({ backups, disk, onChanged }: {
  backups: BackupRecord[];
  disk: { count: number; bytes: number };
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Which backup the delete button is asking about. A backup is the last copy
  // of everything, so it is never one click away.
  const [confirming, setConfirming] = useState('');

  async function remove(backupId: string) {
    setBusy(true);
    setError('');
    try {
      await api.deleteBackup(backupId);
      setConfirming('');
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'نسخه پشتیبان حذف نشد.');
    } finally {
      setBusy(false);
    }
  }

  async function create() {
    setBusy(true);
    setError('');
    try {
      await api.createBackup();
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'نسخه پشتیبان ساخته نشد.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="card-heading">
        <h2>نسخه‌های پشتیبان</h2>
        <button className="button button--soft button--small" onClick={create} disabled={busy}>
          <ShieldCheck size={15} /> {busy ? 'در حال ساخت…' : 'پشتیبان جدید'}
        </button>
      </div>
      {error && <InlineNotice tone="danger">{error}</InlineNotice>}
      <p className="hint">
        {faNumber(disk.count)} نسخه روی سرور، مجموعاً {formatBytes(disk.bytes)}.
      </p>
      {backups.length === 0 ? (
        <p className="hint">هنوز نسخه پشتیبانی ساخته نشده است.</p>
      ) : (
        <div className="data-table">
          <table>
            <thead><tr><th>تاریخ</th><th>حجم</th><th>وضعیت</th><th /></tr></thead>
            <tbody>
              {backups.map((backup) => (
                <tr key={backup.id}>
                  <td>{jalaliDate(backup.createdAt, 'long')}</td>
                  <td>{formatBytes(backup.byteSize)}</td>
                  <td>
                    <StatusBadge status={backup.status} label={statusLabel(backup.status)} />
                    {!backup.available && <small className="hint">فایل روی سرور موجود نیست</small>}
                  </td>
                  <td>
                    <div className="card-actions">
                      {backup.available && backup.status === 'succeeded' && (
                        <a className="button button--ghost button--small" href={api.backupDownloadUrl(backup.id)}>
                          <DownloadSimple size={14} /> دریافت
                        </a>
                      )}
                      {confirming === backup.id ? (
                        <>
                          <button
                            className="button button--danger button--small"
                            disabled={busy}
                            onClick={() => remove(backup.id)}
                          >
                            حذف قطعی
                          </button>
                          <button className="button button--ghost button--small" onClick={() => setConfirming('')}>
                            انصراف
                          </button>
                        </>
                      ) : backup.available && (
                        // Only while there is a file to remove. The row itself
                        // stays either way — who took a backup and then deleted
                        // it is what the trail is for.
                        <button
                          className="button button--ghost button--small"
                          disabled={busy}
                          onClick={() => setConfirming(backup.id)}
                        >
                          <Trash size={14} /> حذف فایل
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function ActivityPanel({ activity }: { activity: ActivityEntry[] }) {
  return (
    <Card>
      <div className="card-heading"><h2>گزارش فعالیت</h2></div>
      {activity.length === 0 ? (
        <p className="hint">فعالیتی ثبت نشده است.</p>
      ) : (
        <div className="data-table">
          <table>
            <thead><tr><th>زمان</th><th>کاربر</th><th>عملیات</th></tr></thead>
            <tbody>
              {activity.map((entry) => (
                <tr key={entry.id}>
                  <td>{jalaliDate(entry.createdAt, 'long')}</td>
                  <td>{entry.actorName}</td>
                  <td>{actionLabel(entry.action)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
